/**
 * lib/manualRun.ts — publishing an update on demand, and watching it happen.
 *
 * **The progress is not client state, and that is the whole design.** `POST /api/jobs/:id/run` is
 * synchronous: it holds the request open for the entire run — fetching commits, calling the model,
 * publishing to every destination — which can take minutes. A naive button would therefore keep its
 * "running" flag in a React state variable, and a refresh, a closed tab, a dropped connection or a
 * navigation would lose it while the run itself carried on regardless. The user would be told nothing is
 * happening while the server was mid-publish.
 *
 * So nothing here remembers anything. The server writes a `ticket_runs` row with `status: 'running'`
 * BEFORE it does any work, holds a lease on it, and updates that row's counters as it goes; the run list is
 * therefore an accurate, live account of what is happening, readable by anyone with access to the business
 * unit. A run in flight is found by looking for it, on every load, by every viewer — so the progress
 * survives a refresh because it was never in the browser to lose. It also means a second person watching
 * the same job sees the same run, which client state could never have given us.
 *
 * Two consequences worth stating:
 *
 *   * **Pressing the button twice is safe.** The server holds a partial unique index on one live run per
 *     job; a second attempt loses the race and is reported as `already_running` rather than starting a
 *     second publish.
 *   * **The POST's own promise is not the signal.** It is allowed to fail — a proxy timeout, a closed
 *     laptop — while the run continues to completion. The watcher below decides when a run is finished by
 *     reading the row, never by awaiting the request that started it.
 */
import { ApiError, api, type Run, type RunListResponse } from "./api";
import { useCallback, useEffect, useRef, useState } from "react";

/** How often the run list is re-read while something is in flight. */
const POLL_MS = 3000;

/** The server's answer to "publish now" — the outcome, and the run row as it stood when it settled. */
export interface ManualRunResponse {
  readonly outcome: {
    readonly status: string;
    readonly runId?: string | null;
    readonly published?: number;
    readonly reason?: string;
  };
  readonly run?: Run;
}

export function startManualRun(jobId: string): Promise<ManualRunResponse | undefined> {
  return api.post<ManualRunResponse>(`/api/jobs/${encodeURIComponent(jobId)}/run`);
}

export function fetchRunList(jobId: string, signal?: AbortSignal, limit = 20): Promise<RunListResponse> {
  return api.get<RunListResponse>(`/api/jobs/${encodeURIComponent(jobId)}/runs`, {
    query: { limit },
    ...(signal ? { signal } : {}),
  });
}

export function activeRun(runs: readonly Run[]): Run | null {
  return runs.find((run) => run.status === "running") ?? null;
}

export interface RunWatch {
  readonly runs: readonly Run[];
  /** The run in flight, if any — from the server, so it is here after a refresh too. */
  readonly active: Run | null;
  /** True from the moment the button is pressed until the server's row confirms it. */
  readonly starting: boolean;
  /** Set when this deployment has no model credential: a fact about the deployment, not a refusal. */
  readonly unavailable: string | null;
  readonly error: ApiError | null;
  readonly start: () => void;
  readonly refresh: () => void;
}

export interface RunWatchOptions {
  readonly jobId: string;
  /** Part of the cache key only: the scope itself travels in the request header. */
  readonly buId: string;
  /** Called once when a run this watcher was following reaches a terminal state. */
  readonly onSettled?: (run: Run) => void;
  /** Test seam. */
  readonly ports?: {
    readonly list: (jobId: string, signal: AbortSignal) => Promise<readonly Run[]>;
    readonly start: (jobId: string) => Promise<ManualRunResponse | undefined>;
  };
  readonly pollMs?: number;
}

const defaultPorts = {
  list: async (jobId: string, signal: AbortSignal): Promise<readonly Run[]> =>
    (await fetchRunList(jobId, signal)).runs,
  start: startManualRun,
};

/**
 * Watch a job's runs, and offer to start one.
 *
 * Polls only while something is in flight. A job sitting idle costs one request on mount and nothing
 * afterwards — which matters because this hook is mounted once per row on the dashboard.
 */
export function useRunWatch(options: RunWatchOptions): RunWatch {
  const { jobId, buId, onSettled, ports = defaultPorts, pollMs = POLL_MS } = options;

  const [runs, setRuns] = useState<readonly Run[]>([]);
  const [starting, setStarting] = useState(false);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);

  // What we were following last time we looked. Comparing against it is how a settlement is noticed
  // exactly once, including the settlement of a run that began before this page was even loaded.
  const following = useRef<string | null>(null);
  const settled = useRef<Set<string>>(new Set());

  /**
   * Read inside the polling closure, which is created once per effect and would otherwise capture
   * `starting` as it stood when the effect ran — i.e. always false, so the poll would stop in the window
   * between the request landing and the row appearing.
   */
  const startingRef = useRef(starting);
  useEffect(() => {
    startingRef.current = starting;
  }, [starting]);

  const refresh = useCallback((): void => setAttempt((current) => current + 1), []);

  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const controller = new AbortController();

    const tick = async (): Promise<void> => {
      try {
        const loaded = await ports.list(jobId, controller.signal);
        if (!live) return;
        setRuns(loaded);
        setError(null);

        const inFlight = activeRun(loaded);
        if (inFlight !== null) {
          following.current = inFlight.id;
          // Confirmed by the server; the optimistic flag has done its job.
          setStarting(false);
        } else if (following.current !== null) {
          const finished = loaded.find((run) => run.id === following.current);
          following.current = null;
          if (finished && !settled.current.has(finished.id)) {
            settled.current.add(finished.id);
            onSettled?.(finished);
          }
        }

        // Poll only while there is something to watch. `starting` counts: between the POST landing and
        // the row appearing there is a window in which nothing is running yet but something will be.
        if (live && (inFlight !== null || startingRef.current)) {
          timer = setTimeout(() => void tick(), pollMs);
        }
      } catch (cause: unknown) {
        if (!live) return;
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setError(cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null));
        // A failed poll is not a reason to stop watching a run that is probably still going.
        if (startingRef.current || following.current !== null) {
          timer = setTimeout(() => void tick(), pollMs);
        }
      }
    };

    void tick();
    return () => {
      live = false;
      controller.abort();
      if (timer !== undefined) clearTimeout(timer);
    };
    // `ports` is a stable module constant by default; `buId` re-keys the whole watch when the scope moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, buId, attempt, pollMs]);

  const start = useCallback((): void => {
    setStarting(true);
    setUnavailable(null);
    setError(null);
    // Deliberately not awaited for the outcome: the row is the truth, and this request may well outlive
    // the page that sent it. Refreshing immediately is what makes the running row appear at once.
    ports
      .start(jobId)
      .then(() => refresh())
      .catch((cause: unknown) => {
        const failure =
          cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null);
        if (failure.status === 503) {
          setUnavailable(failure.userMessage);
          setStarting(false);
          return;
        }
        // Anything else may still have started a run — the request could have been cut off in transit —
        // so the list is re-read rather than assumed. `starting` is cleared by the poll either way.
        setError(failure);
        refresh();
      });
    refresh();
  }, [jobId, ports, refresh]);

  return { runs, active: activeRun(runs), starting, unavailable, error, start, refresh };
}
